// Team management — invite people, change roles, remove them.
//
// The invite link is shown for copying as well as emailed. That's lifted from
// Venteza's console and it earns its place here: SendGrid domain
// authentication for stallside.io isn't set up, so invite mail can land in
// spam. Being able to paste the link into a text message means an invite is
// never blocked on deliverability.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatPhoneInput, isCompletePhone, displayPhone, telHref } from '../../lib/phone';
import InfoOnlyBadge from '../../components/ui/InfoOnlyBadge';
import { isRealEmail } from '../../lib/email';

// "Owner" is dangerously overloaded in this app: here it means the barn
// proprietor (full access, including this page), but the Horse owners directory
// calls its CLIENTS "owners" too. Labeled explicitly so nobody hands over the
// barn while trying to add someone who owns a horse.
const ROLE_LABELS = {
  owner: 'Barn owner',
  manager: 'Manager',
  trainer: 'Trainer',
  groom: 'Groom',
  client: 'Client (owns a horse here)',
  rider: 'Rider (rides a horse here)',
  service_provider: 'Service provider',
};

const ROLE_HELP = {
  owner: 'Full control of the barn, including managing this team page and billing. Not the same as owning a horse.',
  manager: 'Runs the operation. Everything except owner-only settings.',
  trainer: 'Their horses, schedules and lessons. No financials.',
  groom: 'Daily board, horse profiles, care cards.',
  client: 'A horse owner. Sees only their own horses — no barn management. This is the one you want for the people in the Horse owners directory.',
  rider: 'Rides here but doesn’t own a horse. Sees the horses they’re scheduled on, and nothing else.',
  service_provider: 'Vet, farrier or similar. Read-only on the horses they work with.',
};

export default function TeamPage() {
  const qc = useQueryClient();
  const { activeRole } = useAuth();
  const canManage = activeRole === 'owner' || activeRole === 'manager';
  const [showInvite, setShowInvite] = useState(false);
  const [banner, setBanner] = useState(null); // { email, link, emailed }
  const [err, setErr] = useState('');

  const teamQ = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get('/team'),
    enabled: canManage,
    retry: false,
  });

  const members = teamQ.data?.members ?? [];
  // People we could text but currently can't reach any other way. This is the
  // gap that makes every notification in the app inert for most of the barn.
  const textableGap = members.filter(
    (m) => m.reachable === 'sms' && !m.smsOptIn && !m.smsStopped,
  );
  const you = teamQ.data?.you;
  const yourRole = teamQ.data?.yourRole;

  const refresh = () => qc.invalidateQueries({ queryKey: ['team'] });

  const changeRole = useMutation({
    mutationFn: ({ userId, role }) => api.patch(`/team/${userId}`, { role }),
    onSuccess: refresh,
    onError: (e) => setErr(friendly(e)),
  });

  const remove = useMutation({
    mutationFn: (userId) => api.del(`/team/${userId}`),
    onSuccess: refresh,
    onError: (e) => setErr(friendly(e)),
  });

  const setSms = useMutation({
    mutationFn: ({ userIds, enabled }) => api.post('/team/sms', { userIds, enabled }),
    onSuccess: refresh,
    onError: (e) => setErr(friendly(e)),
  });

  const [editing, setEditing] = useState(null); // userId being edited

  const saveDetails = useMutation({
    mutationFn: ({ userId, ...fields }) => api.patch(`/team/${userId}`, fields),
    onSuccess: () => { setEditing(null); refresh(); },
    onError: (e) => setErr(friendly(e)),
  });

  const resend = useMutation({
    mutationFn: (userId) => api.post(`/team/${userId}/link`),
    onSuccess: (data, userId) => {
      const m = members.find((x) => x.userId === userId);
      setBanner({
        email: m?.email ?? '', link: data.link, emailed: data.emailed,
        texted: data.texted, phone: data.phone, isReset: data.isReset,
      });
      setErr('');
    },
    onError: (e) => setErr(friendly(e)),
  });

  if (!canManage) {
    return (
      <div className="max-w-3xl mx-auto p-4">
        <div className="card p-8 text-center text-ink-500">
          Team settings are for owners and managers.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 pb-24 sm:pb-8">
      <header className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Team</h1>
          <p className="text-sm text-ink-500">Everyone with access to this barn.</p>
        </div>
        {!showInvite && (
          <button onClick={() => { setShowInvite(true); setBanner(null); }} className="btn-primary shrink-0">
            + Invite
          </button>
        )}
      </header>

      {banner && <InviteBanner {...banner} onClose={() => setBanner(null)} />}
      {err && <p className="text-sm text-red-600 mb-3">{err}</p>}

      {textableGap.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 mb-4">
          <p className="text-sm text-amber-900">
            <strong>{textableGap.length}</strong>{' '}
            {textableGap.length === 1 ? 'person has' : 'people have'} a mobile number but no
            email address we can use. Reminders and the weekly schedule won't reach them
            unless texting is switched on.
          </p>
          <button
            type="button"
            className="btn-primary text-sm mt-3"
            disabled={setSms.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `Switch texting on for ${textableGap.length} ` +
                    `${textableGap.length === 1 ? 'person' : 'people'}?

` +
                    'They will get lesson reminders and schedule messages by text ' +
                    'instead of email. Anyone who replies STOP is removed ' +
                    'automatically and cannot be re-added here.',
                )
              ) {
                setSms.mutate({ userIds: textableGap.map((m) => m.userId), enabled: true });
              }
            }}
          >
            {setSms.isPending ? 'Switching on…' : 'Turn On Texting For Them'}
          </button>
        </div>
      )}

      {showInvite && (
        <InviteForm
          yourRole={yourRole}
          onCancel={() => setShowInvite(false)}
          onDone={(res, email) => {
            setShowInvite(false);
            setBanner({ email, link: res.link, emailed: res.emailed, texted: res.texted, phone: res.phone });
            setErr('');
            refresh();
          }}
          onError={(e) => setErr(friendly(e))}
        />
      )}

      {teamQ.isLoading ? (
        <div className="card p-8 text-center text-ink-400">Loading…</div>
      ) : (
        <ul className="card divide-y divide-ink-100">
          {members.map((m) => (
            <MemberRow
              key={m.userId}
              m={m}
              isYou={m.userId === you}
              yourRole={yourRole}
              busy={changeRole.isPending || remove.isPending || resend.isPending}
              onRole={(role) => { setErr(''); changeRole.mutate({ userId: m.userId, role }); }}
              onSetSms={(args) => { setErr(''); setSms.mutate(args); }}
              onRemove={() => {
                setErr('');
                if (window.confirm(`Remove ${m.name || m.email} from this barn? Their account and any other barn stay untouched.`)) {
                  remove.mutate(m.userId);
                }
              }}
              onResend={() => {
                setErr('');
                // Resending an unclaimed invite is harmless. Sending a reset to
                // a live account is worth a beat — it lands in their inbox.
                if (
                  m.status === 'invited' ||
                  window.confirm(`Send ${m.name || m.email} a link to reset their password?`)
                ) {
                  resend.mutate(m.userId);
                }
              }}
              editing={editing === m.userId}
              onEdit={() => { setErr(''); setEditing(editing === m.userId ? null : m.userId); }}
              onSave={(fields) => { setErr(''); saveDetails.mutate({ userId: m.userId, ...fields }); }}
              saving={saveDetails.isPending}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function MemberRow({ m, isYou, yourRole, busy, onRole, onRemove, onResend, onSetSms, editing, onEdit, onSave, saving }) {
  // Only an owner can touch another owner — otherwise a manager could demote
  // the owner and take the barn. Mirrors canAssign() on the server.
  const mayEdit = !isYou && (yourRole === 'owner' || m.role !== 'owner');
  const assignable = Object.keys(ROLE_LABELS).filter((r) => yourRole === 'owner' || r !== 'owner');

  // You can always fix your own name and number; role changes stay blocked.
  const mayEditDetails = isYou || mayEdit;

  return (
    <li className="p-3">
      {/* On a phone the identity gets its own full-width line and the controls
          wrap underneath. Side by side, the shrink-0 control cluster took the
          whole row and squeezed names and emails down to "S…" / "js…". */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3">
      <div className="min-w-0 sm:flex-1">
        <div className="font-medium text-ink-900 break-words sm:truncate">
          {m.name || m.email}
          {isYou && <span className="text-ink-400 font-normal"> · you</span>}
        </div>
        <div className="text-sm text-ink-500 break-words sm:truncate">
          {isRealEmail(m.email)
            ? m.email
            : <span className="text-ink-300">No email</span>}
        </div>
        {m.phone && (
          <div className="text-sm">
            {/* Same as the owners directory: a tappable number on a handset. */}
            {telHref(m.phone) ? (
              <a
                href={telHref(m.phone)}
                className="text-ink-400 hover:text-ink-900 underline underline-offset-2"
              >
                {displayPhone(m.phone)}
              </a>
            ) : (
              <span className="text-ink-400">{displayPhone(m.phone)}</span>
            )}
          </div>
        )}
        <StatusPill status={m.status} />
        <SmsPill member={m} onSet={onSetSms} />
      </div>

      <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
        {mayEdit ? (
          <select
            className="input w-auto text-sm"
            value={m.role}
            disabled={busy}
            onChange={(e) => onRole(e.target.value)}
            aria-label={`Role for ${m.name || m.email}`}
          >
            {assignable.map((r) => (
              <option key={r} value={r}>{ROLE_LABELS[r]}</option>
            ))}
          </select>
        ) : (
          <span className="text-sm px-2 py-1 rounded-full bg-ink-100 text-ink-600">
            {ROLE_LABELS[m.role] ?? m.role}
          </span>
        )}

        {mayEditDetails && (
          <button
            onClick={onEdit}
            disabled={busy}
            aria-expanded={editing}
            className="text-sm text-ink-600 hover:text-ink-900 underline"
          >
            {editing ? 'Close' : 'Edit'}
          </button>
        )}

        {mayEditDetails && (
          <button
            onClick={onResend}
            disabled={busy}
            className="text-sm text-ink-600 hover:text-ink-900 underline whitespace-nowrap"
            title={
              m.status === 'invited'
                ? 'Send their invite link again'
                : 'Email/text them a link to reset their password'
            }
          >
            {m.status === 'invited' ? 'Resend' : 'Reset Password'}
          </button>
        )}

        {mayEdit && (
          <button
            onClick={onRemove}
            disabled={busy}
            className="text-ink-300 hover:text-red-600 px-1"
            aria-label={`Remove ${m.name || m.email}`}
          >
            ✕
          </button>
        )}
        </div>
      </div>

      {editing && <EditDetails m={m} saving={saving} onSave={onSave} onCancel={onEdit} />}
    </li>
  );
}

// Inline editor for name / email / phone. Email is locked once the person has
// set a password — at that point it is their login, and letting a manager
// repoint it would be an account takeover. Unclaimed invites stay editable,
// which is what the imported @placeholder.stallside.io clients need.
function EditDetails({ m, saving, onSave, onCancel }) {
  const [name, setName] = useState(m.name || '');
  const [email, setEmail] = useState(m.email || '');
  const [phone, setPhone] = useState(displayPhone(m.phone) || '');

  const emailLocked = m.status !== 'invited';
  const phoneOk = isCompletePhone(phone);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!phoneOk) return;
        onSave({ name, email, phone });
      }}
      className="mt-3 rounded-md bg-ink-50 p-3 space-y-3"
    >
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1">Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1">Mobile</label>
          <input
            className="input"
            type="tel"
            inputMode="tel"
            placeholder="(555) 123-4567"
            value={phone}
            onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
          />
          {!phoneOk && <p className="text-xs text-amber-700 mt-1">A US mobile is 10 digits.</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-ink-700 mb-1">Email</label>
        <input
          className="input"
          type="email"
          value={email}
          disabled={emailLocked}
          onChange={(e) => setEmail(e.target.value)}
        />
        <p className="text-xs text-ink-400 mt-1">
          {emailLocked
            ? 'They’ve set a password, so this is their login — only they can change it.'
            : 'This is where their invite goes. Editable until they set a password.'}
        </p>
      </div>

      <div className="flex gap-2">
        <button type="submit" disabled={saving || !phoneOk} className="btn-primary">
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-ink-500 hover:text-ink-800 px-2">
          Cancel
        </button>
      </div>
    </form>
  );
}

function StatusPill({ status }) {
  if (status === 'contact_only') {
    return <span className="inline-block mt-1"><InfoOnlyBadge /></span>;
  }
  if (status === 'invited') {
    return (
      <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 whitespace-nowrap">
        Invited — hasn’t set a password
      </span>
    );
  }
  if (status === 'never_signed_in') {
    return (
      <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-ink-100 text-ink-500 whitespace-nowrap">
        Never signed in
      </span>
    );
  }
  return null;
}

function InviteForm({ yourRole, onCancel, onDone, onError }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('groom');

  const phoneOk = isCompletePhone(phone);

  const invite = useMutation({
    mutationFn: () =>
      api.post('/team/invite', {
        email: email.trim(),
        name: name.trim() || undefined,
        role,
        // Digits only; the server normalizes to E.164 for Twilio.
        phone: phone.trim() || undefined,
      }),
    onSuccess: (res) => onDone(res, email.trim()),
    onError,
  });

  const roles = Object.keys(ROLE_LABELS).filter((r) => yourRole === 'owner' || r !== 'owner');

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (email.trim()) invite.mutate(); }}
      className="card p-4 mb-4 space-y-3"
    >
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="inv-name" className="block text-sm font-medium text-ink-700 mb-1">
            Name <span className="font-normal text-ink-400">(optional)</span>
          </label>
          <input id="inv-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label htmlFor="inv-email" className="block text-sm font-medium text-ink-700 mb-1">Email</label>
          <input
            id="inv-email"
            type="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="them@example.com"
            autoFocus
          />
        </div>
      </div>

      <div>
        <label htmlFor="inv-phone" className="block text-sm font-medium text-ink-700 mb-1">
          Mobile <span className="font-normal text-ink-400">(optional)</span>
        </label>
        <input
          id="inv-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          className="input"
          placeholder="(555) 123-4567"
          value={phone}
          onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
          aria-invalid={!phoneOk}
        />
        <p className={`text-xs mt-1 ${phoneOk ? 'text-ink-400' : 'text-amber-700'}`}>
          {phoneOk
            ? 'Add a mobile and we’ll text them the link as well as emailing it.'
            : 'That looks incomplete — a US mobile is 10 digits.'}
        </p>
      </div>

      <div>
        <label htmlFor="inv-role" className="block text-sm font-medium text-ink-700 mb-1">Role</label>
        <select id="inv-role" className="input" value={role} onChange={(e) => setRole(e.target.value)}>
          {roles.map((r) => (
            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
          ))}
        </select>
        <p className="text-xs text-ink-400 mt-1">{ROLE_HELP[role]}</p>
        {role === 'owner' && (
          <p className="mt-2 text-xs rounded-md bg-amber-50 text-amber-800 p-2">
            <strong>This gives away control of the barn.</strong> A barn owner can
            manage this team page, change your role and remove you. If you just mean
            someone who owns a horse here, pick <strong>Client</strong> instead.
          </p>
        )}
      </div>

      <p className="text-xs text-ink-400">
        They get an email with a link to set their own password — you never handle it.
        The link works once and expires in 7 days.
      </p>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={invite.isPending || !email.trim() || !phoneOk}
          className="btn-primary"
        >
          {invite.isPending ? 'Inviting…' : phone.trim() ? 'Send Invite + Text' : 'Send Invite'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-ink-500 hover:text-ink-800 px-2">
          Cancel
        </button>
      </div>
    </form>
  );
}

// Copyable invite link. Shown whether or not the email went out, so a bounced
// or spam-filtered invite is never a dead end — text it to them instead.
function InviteBanner({ email, link, emailed, texted, phone, isReset, onClose }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const el = document.getElementById('invite-link');
      el?.select();
      document.execCommand?.('copy');
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="card p-4 mb-4 border-emerald-200 bg-emerald-50/50">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <strong className="text-ink-900">
            {isReset ? `Reset link sent to ${email}` : `Invited ${email}`}
          </strong>
          <p className="text-sm text-ink-600 mt-0.5">
            {sentVia(emailed, texted, phone)}
          </p>
        </div>
        <button onClick={onClose} className="text-ink-400 hover:text-ink-700 shrink-0" aria-label="Dismiss">
          ✕
        </button>
      </div>
      <div className="flex gap-2 mt-3">
        <input
          id="invite-link"
          readOnly
          value={link}
          onClick={(e) => e.target.select()}
          className="input flex-1 text-xs font-mono"
        />
        <button onClick={copy} type="button" className="btn-secondary shrink-0 whitespace-nowrap">
          {copied ? 'Copied ✓' : 'Copy link'}
        </button>
      </div>
      <p className="text-xs text-ink-400 mt-2">
        Works once, expires in 7 days. Safe to text — it only lets them set their own password.
      </p>
    </div>
  );
}

// Say precisely what went out, so a silent SMS failure is never mistaken for
// a delivered text.
function sentVia(emailed, texted, phone) {
  if (emailed && texted) return `We emailed them a link and texted it to ${displayPhone(phone)}.`;
  if (emailed && phone && !texted)
    return 'We emailed them a link. The text didn’t send — copy the link below and send it yourself.';
  if (emailed) return 'We emailed them a link to set their password.';
  if (texted) return `The email didn’t send, but we texted the link to ${displayPhone(phone)}.`;
  return 'Nothing sent — copy the link below and send it to them directly.';
}

function friendly(e) {
  switch (e?.body?.error || e?.message || '') {
    case 'already_member':
      return 'That person is already in this barn.';
    case 'invalid_email':
      return 'Enter a valid email address.';
    case 'email_locked':
      return 'They’ve already set a password — only they can change their own email.';
    case 'email_taken':
      return 'Another account already uses that email.';
    case 'invalid_phone':
      return 'That mobile number doesn’t look right — 10 digits for a US number.';
    case 'invalid_role':
      return 'Pick a role.';
    case 'role_not_allowed':
      return 'Only an owner can add or change another owner.';
    case 'last_owner':
      return 'This is the barn’s only owner — make someone else an owner first.';
    case 'cannot_change_own_role':
      return 'You can’t change your own role.';
    case 'cannot_remove_self':
      return 'You can’t remove yourself from the barn.';
    case 'forbidden':
      return 'Only owners and managers can manage the team.';
    default:
      return 'Something went wrong. Try again.';
  }
}

/**
 * Whether this person can be texted, and a way to change it.
 *
 * Three states worth telling apart. Opted out is not the same as off: the barn
 * cannot switch it back on, only the person can, by texting START. Saying so
 * here saves a manager clicking a toggle that will never take.
 */
function SmsPill({ member, onSet }) {
  if (!member.phone) return null;

  if (member.smsStopped) {
    return (
      <span
        className="text-xs px-2 py-0.5 rounded-full bg-ink-100 text-ink-500"
        title="They replied STOP. Only they can undo this, by texting START to the barn number."
      >
        Opted out of texts
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onSet({ userIds: [member.userId], enabled: !member.smsOptIn })}
      className={
        'text-xs px-2 py-0.5 rounded-full border transition-colors ' +
        (member.smsOptIn
          ? 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100'
          : 'bg-white text-ink-500 border-ink-200 hover:bg-ink-50')
      }
      title={
        member.smsOptIn
          ? 'Reminders and schedule messages can be texted to them.'
          : 'Texting is off for them.'
      }
    >
      {member.smsOptIn ? 'Texts on' : 'Texts off'}
    </button>
  );
}
